import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@environment';

// Handles Google OAuth2 sign-in using the implicit redirect flow.
// We use redirect instead of the GIS popup because Chrome on iOS (WebKit) does not
// support FedCM or cross-origin postMessage from popup tabs, which breaks the popup flow.
@Injectable({ providedIn: 'root' })
export class GoogleAuthService {
  private readonly platformId = inject(PLATFORM_ID);

  // Redirects the browser to Google's OAuth2 authorization endpoint.
  // After the user authenticates, Google redirects back to /auth/google/callback
  // with the id_token in the URL fragment (#id_token=...).
  initiateRedirect(): void {
    // Skip on the server side during SSR — window is not available there.
    if (!isPlatformBrowser(this.platformId)) return;

    // The redirect URI must exactly match one of the URIs registered in Google Cloud Console
    // under the OAuth 2.0 Client ID → Authorized redirect URIs.
    const redirectUri = `${window.location.origin}/auth/google/callback`;

    // A random nonce is required by the OpenID Connect spec to prevent replay attacks.
    // Google embeds it in the returned id_token; the backend can optionally verify it.
    const nonce = crypto.randomUUID();

    const params = new URLSearchParams({
      client_id: environment.googleLoginProviderId,
      redirect_uri: redirectUri,
      // id_token is returned directly in the URL fragment without a server round-trip (implicit flow).
      response_type: 'id_token',
      // openid is required; email and profile provide basic user info claims inside the token.
      scope: 'openid email profile',
      nonce,
    });

    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
  }

  // No-op: the OAuth2 redirect flow has no client-side session to clear.
  // Kept so AuthenticationService.logout() can call it without conditional checks.
  signOut(): void {}
}
